/**
 * Market data (the evidence pack) — only what went wrong: each part whose status isn't `ok`, with
 * the server's sentence, and each of the run's `price_notes`. A price that disagrees with the DataPack
 * is a notice, never a block (SPEC_3 Decision 1), and the run uses the DataPack's close.
 *
 * A pack with every part ok draws nothing at all, and the console leaves the panel out (`troubled`).
 */

import type { Today } from "../../api/types/competition";
import { useT } from "../../i18n/useT";

/** The parts that aren't ok. A pack nobody has built yet isn't a problem of its own: the run says so. */
function gaps(today: Today) {
  return Object.entries(today.pack?.parts ?? {}).filter(([, p]) => p.status !== "ok");
}

export function troubled(today: Today): boolean {
  return gaps(today).length > 0 || (today.run?.price_notes ?? []).length > 0;
}

export function Pack({ today }: { today: Today }) {
  const { t } = useT();
  const notes = today.run?.price_notes ?? [];

  return (
    <ul className="parts">
      {gaps(today).map(([name, part]) => (
        <li key={name} className="part" data-part={name} data-status={part.status ?? "none"}>
          <span className="part__name">{name}</span>
          <span className="part__status">{part.status ?? "—"}</span>
          <span className="part__says">{part.says}</span>
        </li>
      ))}
      {notes.map((note, i) => (
        <li key={`note-${i}`} className="part part--note" data-price-note="">
          <span className="part__name">{t("pack.priceNote")}</span>
          <span className="part__says">{note}</span>
        </li>
      ))}
    </ul>
  );
}
